import { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight, Sparkles, Scale, Heart, TrendingUp, AlertCircle } from 'lucide-react';
import { Flame } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from 'react-router-dom';

import { usePopularSubcategories } from "../../../hooks/usePopularSubcategories";

interface PopularCategory {
  id: number;
  name: string;
  description: string;
  parentCategory: string;
  icon: any;
  gradient: string;
  badge: 'Trending' | 'Popular';
}

// 🟢 MAPPING DATABASE NAMES TO ROUTE SLUGS
const SUBCATEGORY_SLUG_MAP: Record<string, string> = {
  'love & relationship astrology': 'love-and-relationship-astrology',
  'love & relationship': 'love-and-relationship-astrology',
  'career & business astrology experts': 'career-and-business-astrology',
  'career & business astrology': 'career-and-business-astrology',
  'vedic astrologers': 'vedic-astrology',
  'tarot card readers': 'tarot-card-readers',
  'lal kitab experts': 'lal-kitab-experts',
  'numerologists': 'numerologists',
  'palmists (hast rekha experts)': 'palmists',
  'face reading experts (samudrik shastra)': 'face-reading-experts',
  'western astrologers': 'western-astrologers',
  'nadi astrologers': 'nadi-astrologers',
  'health astrology': 'health-astrology',
  'remedies & vastu consultants': 'remedies-and-vastu-consultants',
  'family and marriage law': 'family-law',
  'family law': 'family-law',
  'criminal law': 'criminal-defense',
  'criminal defense lawyers': 'criminal-defense',
  'real estate law': 'real-estate-law',
  'real estate & property lawyers': 'real-estate-law',
  'consumer protection law': 'consumer-protection-law',
  'business law': 'business-law',
  'general physician': 'general-physician',
  'dermatology': 'dermatology',
  'gynecology': 'gynecology',
};

// Helper to convert subcategory name into a slug
const getSubcategorySlug = (name: string): string => {
  const lower = name.toLowerCase().trim();
  if (SUBCATEGORY_SLUG_MAP[lower]) {
    return SUBCATEGORY_SLUG_MAP[lower];
  }
  return lower
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-');
};

// Icon and gradient mapping based on parent category
const categoryStyles: Record<string, { icon: any; gradient: string }> = {
  'Astrology': {
    icon: Sparkles,
    gradient: 'from-purple-500 to-pink-500'
  },
  'Lawyer': {
    icon: Scale,
    gradient: 'from-blue-500 to-indigo-500'
  },
  'Doctor': {
    icon: Heart,
    gradient: 'from-pink-500 to-rose-500'
  },
  // Default fallback
  'default': {
    icon: TrendingUp,
    gradient: 'from-orange-500 to-red-500'
  }
};

interface PopularCategoriesProps {
  onSelectCategory: (categoryId: string) => void;
}

export function PopularCategories({ onSelectCategory }: PopularCategoriesProps) {
  const { items, loading } = usePopularSubcategories();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [slidesToShow, setSlidesToShow] = useState(3);

  // Map API items to PopularCategory format
  const popularCategories: PopularCategory[] = items.map(item => {
    const style = categoryStyles[item.parentCategory] || categoryStyles['default'];
    return {
      id: item.id,
      name: item.name,
      description: item.description || '',
      parentCategory: item.parentCategory,
      icon: style.icon,
      gradient: style.gradient,
      badge: item.badge
    };
  });

  useEffect(() => {
    const updateSlidesToShow = () => {
      if (window.innerWidth < 640) {
        setSlidesToShow(1);
      } else if (window.innerWidth < 1024) {
        setSlidesToShow(2);
      } else {
        setSlidesToShow(3);
      }
    };

    updateSlidesToShow();
    window.addEventListener('resize', updateSlidesToShow);
    return () => window.removeEventListener('resize', updateSlidesToShow);
  }, []);

  useEffect(() => {
    if (popularCategories.length === 0) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % popularCategories.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [popularCategories.length]);

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % popularCategories.length);
  };

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev - 1 + popularCategories.length) % popularCategories.length);
  };

  const getVisibleCategories = () => {
    const visible = [];
    for (let i = 0; i < slidesToShow; i++) {
      visible.push(popularCategories[(currentIndex + i) % popularCategories.length]);
    }
    return visible;
  };

  return (
    <div className="mb-16 font-['Poppins']">
      <h2 className="flex items-center gap-2 text-lg sm:text-xl font-normal text-gray-900 mb-6 sm:mb-8">
        <span className="text-base sm:text-xl">Hot & Trending Specializations</span>
        <motion.span
          animate={{ 
            opacity: [1, 0.7, 1],
            filter: [
              "brightness(1) drop-shadow(0 0 2px rgb(249, 115, 22))",
              "brightness(1.3) drop-shadow(0 0 8px rgb(249, 115, 22))",
              "brightness(1) drop-shadow(0 0 2px rgb(249, 115, 22))"
            ]
          }}
          transition={{ 
            repeat: Infinity, 
            duration: 2,
            ease: "easeInOut"
          }}
          className="inline-block flex-shrink-0"
        >
          <Flame className="w-4 h-4 sm:w-5 sm:h-5 text-orange-500" />
        </motion.span>
      </h2>

      <div className="relative px-12">
        {/* Navigation Buttons */}
        <button
          onClick={handlePrev}
          disabled={loading || popularCategories.length === 0}
          className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ChevronLeft className="w-5 h-5 text-gray-700" />
        </button>
        <button
          onClick={handleNext}
          disabled={loading || popularCategories.length === 0}
          className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ChevronRight className="w-5 h-5 text-gray-700" />
        </button>

        {/* Carousel */}
        <div className="overflow-hidden">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {loading ? (
              <>
                {Array.from({ length: slidesToShow }).map((_, index) => (
                  <SkeletonCard key={index} />
                ))}
              </>
            ) : popularCategories.length === 0 ? (
              <>
                {Array.from({ length: slidesToShow }).map((_, index) => (
                  <ErrorCard key={index} />
                ))}
              </>
            ) : (
              <AnimatePresence mode="popLayout">
                {getVisibleCategories().map((category) => (
                  <motion.div
                    key={`${category.id}-${currentIndex}`}
                    initial={{ opacity: 0, x: 100 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -100 }}
                    transition={{ duration: 0.5 }}
                  >
                    <PopularCategoryCard category={category} onSelect={onSelectCategory} />
                  </motion.div>
                ))}
              </AnimatePresence>
            )}
          </div>
        </div>

        {/* Dots Indicator */}
        {!loading && popularCategories.length > 0 && (
          <div className="flex justify-center gap-2 mt-8">
            {popularCategories.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`w-2.5 h-2.5 rounded-full transition-all ${
                  index === currentIndex ? 'bg-blue-600 w-8' : 'bg-gray-300'
                }`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Skeleton Loading Card
function SkeletonCard() {
  return (
    <div className="relative bg-gradient-to-br from-gray-200 to-gray-100 rounded-2xl p-8 shadow-xl overflow-hidden animate-pulse">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 right-0 w-32 h-32 bg-white rounded-full -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-24 h-24 bg-white rounded-full translate-y-1/2 -translate-x-1/2"></div>
      </div>
      <div className="relative mb-4">
        <div className="inline-block w-20 h-6 bg-gray-300 rounded-full"></div>
      </div>
      <div className="relative mb-4">
        <div className="w-16 h-16 rounded-xl bg-gray-300"></div>
      </div>
      <div className="relative mb-2">
        <div className="h-6 bg-gray-300 rounded w-3/4"></div>
      </div>
      <div className="relative mb-4 space-y-2">
        <div className="h-4 bg-gray-300 rounded w-full"></div>
        <div className="h-4 bg-gray-300 rounded w-5/6"></div>
      </div>
      <div className="relative mb-4">
        <div className="h-4 bg-gray-300 rounded w-1/2"></div>
      </div>
      <div className="relative w-full h-10 bg-gray-300 rounded-lg"></div>
    </div>
  );
}

// Error Card when data fails to load
function ErrorCard() {
  return (
    <div className="relative bg-gradient-to-br from-red-50 to-orange-50 rounded-2xl p-8 shadow-xl overflow-hidden border-2 border-red-200">
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 right-0 w-32 h-32 bg-red-500 rounded-full -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-24 h-24 bg-orange-500 rounded-full translate-y-1/2 -translate-x-1/2"></div>
      </div>
      <div className="relative mb-4 flex justify-center">
        <div className="w-16 h-16 rounded-xl bg-red-100 flex items-center justify-center">
          <AlertCircle className="w-8 h-8 text-red-500" />
        </div>
      </div>
      <h3 className="relative text-gray-800 text-center mb-3 font-semibold">Unable to Load</h3>
      <p className="relative text-gray-600 text-sm text-center mb-6">
        We're having trouble loading popular categories right now.
      </p>
      <div className="relative bg-white/60 backdrop-blur-sm rounded-lg p-4 text-center">
        <p className="text-xs text-gray-700 mb-2">
          <span className="font-semibold">👨‍💻 Mr. Developer is fighting a nasty bug with his stick! </span>
        </p>
        <p className="text-xs text-gray-500">
          Stay tuned, Coming soon.
        </p>
      </div>
    </div>
  );
}

function PopularCategoryCard({ category }: { category: PopularCategory; onSelect: (id: string) => void }) {
  const Icon = category.icon;
  const navigate = useNavigate();

  // 🟢 DIRECT SUBCATEGORY ROUTING LOGIC
  const handleViewExperts = () => {
    if (!category.parentCategory || !category.name) return;
    
    const formattedCat = category.parentCategory.toLowerCase();
    const slug = getSubcategorySlug(category.name);
    
    // Direct navigation to /experts/:category/:subCategorySlug
    navigate(`/experts/${formattedCat}/${slug}`);
  };

  return (
    <motion.div
      whileHover={{ y: -8 }}
      transition={{ type: 'spring', stiffness: 300 }}
      className="cursor-pointer"
      onClick={handleViewExperts}
    >
      <div className={`relative bg-gradient-to-br ${category.gradient} rounded-2xl p-8 text-white shadow-xl overflow-hidden group`}>
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-32 h-32 bg-white rounded-full -translate-y-1/2 translate-x-1/2"></div>
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-white rounded-full translate-y-1/2 -translate-x-1/2"></div>
        </div>

        {/* Badge */}
        <div className="relative mb-4">
          <span className="inline-block px-3 py-1 bg-white/30 backdrop-blur-sm rounded-full text-xs">
            {category.badge}
          </span>
        </div>

        {/* Icon */}
        <div className="relative mb-4">
          <div className="w-16 h-16 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center group-hover:scale-110 transition-transform">
            <Icon className="w-8 h-8 text-white" />
          </div>
        </div>

        {/* Content */}
        <h3 className="relative text-white mb-2">{category.name}</h3>
        <p className="relative text-white/90 text-sm mb-4">{category.description}</p>

        {/* Parent Category */}
        <div className="relative text-sm text-white/90 mb-4">
          <span className="font-medium">{category.parentCategory}</span>
        </div>

        {/* Explore Button */}
        <button 
          onClick={(e) => {
            e.stopPropagation();
            handleViewExperts();
          }}
          className="relative w-full px-4 py-2.5 bg-white text-gray-900 rounded-lg hover:bg-gray-50 transition-colors text-sm font-semibold cursor-pointer"
        >
          Explore Now
        </button>
      </div>
    </motion.div>
  );
}